<template>
    <section class="signature-card" :class="cardClasses">
        <div class="card-top">
            <div class="title-group">
                <div class="icon-box" :class="theme">
                    <i v-if="theme === 'day'" class="pi pi-sun"></i>
                    <i v-else class="pi pi-moon"></i>
                </div>

                <div>
                    <h2>{{ title }}</h2>
                    <p>{{ role }}</p>
                </div>
            </div>

            <i v-if="isSigned" class="pi pi-check-circle status-check"></i>
        </div>

        <div v-if="isSigned" class="info-row">
            <div class="info-block">
                <span class="label">Signed By</span>
                <strong>{{ signName }}</strong>
            </div>

            <div class="info-block">
                <span class="label">Signed At</span>
                <strong>{{ signDate }}</strong>
            </div>
        </div>

        <div v-if="showWarning" class="warning-banner">
            {{ warningText }}
        </div>

        <button class="signature-box" :class="{ clickable: canSign, locked: !canSign }" :disabled="!canSign"
            @click="openModal">
            <template v-if="isSigned">
                <img :src="signPicture" alt="Signature" class="signature-image" />
            </template>

            <template v-else>
                <div class="empty-signature-space">
                    <!-- <div class="signature-placeholder-line"></div> -->
                    <p v-if="canSign" class="signature-placeholder-text">Tap to sign here</p>
                    <p v-else-if="inTimeRange && !allChecklistCompleted" class="signature-placeholder-text">
                        Complete all checklists first
                    </p>
                    <p v-else class="signature-placeholder-text">
                        Signature unavailable
                    </p>
                </div>
            </template>
        </button>

        <div v-if="showModal && canSign" class="modal-overlay" @click.self="closeModal">
            <div class="modal-card">
                <div class="modal-header">
                    <h3>{{ title }}</h3>
                    <button class="close-btn" @click="closeModal">✕</button>
                </div>

                <p class="modal-subtitle">Sign as {{ signName || 'Team Leader' }}</p>

                <div class="pad-wrap">
                    <canvas ref="signatureCanvas" class="signature-pad" width="700" height="220"
                        @mousedown="startDrawing" @mousemove="draw" @mouseup="stopDrawing" @mouseleave="stopDrawing"
                        @touchstart.prevent="startDrawing" @touchmove.prevent="draw" @touchend.prevent="stopDrawing" />
                </div>

                <div class="modal-actions">
                    <button class="secondary-btn" @click="clearCanvas">Clear</button>
                    <button class="primary-btn" @click="confirmSignature">Confirm Signature</button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'ShiftSignatureCard',
    props: {
        title: String,
        role: {
            type: String,
            default: 'Team Leader'
        },
        theme: String,
        signDate: String,
        signPicture: String,
        signName: String,
        warningText: String,
        viewOnly: {
            type: Boolean,
            default: false
        },
        inTimeRange: {
            type: Boolean,
            default: false
        },
        allChecklistCompleted: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showModal: false,
            isDrawing: false,
            hasStroke: false
        }
    },
    computed: {
        ...mapState(["userInfo"]),
        isSigned() {
            return !!this.signPicture
        },
        canSign() {
            if (this.viewOnly) return false
            if (this.isSigned) return false
            return this.inTimeRange && this.allChecklistCompleted && this.$store.state.userInfo.role == '3'
        },
        showWarning() {
            return !this.isSigned && this.inTimeRange && !this.allChecklistCompleted
        },
        cardClasses() {
            if (this.theme === 'day') {
                return this.canSign || this.isSigned ? 'day-active' : 'default-card'
            }
            return this.canSign || this.isSigned ? 'night-active' : 'night-inactive'
        }
    },
    methods: {
        openModal() {
            if (!this.canSign) return
            this.showModal = true
            this.$nextTick(() => {
                this.setupCanvas()
            })
        },
        closeModal() {
            this.showModal = false
        },
        setupCanvas() {
            var canvas = this.$refs.signatureCanvas
            if (!canvas) return

            var ctx = canvas.getContext('2d')
            ctx.lineWidth = 2.5
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
            ctx.strokeStyle = '#2f2f2f'
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            this.hasStroke = false
        },
        getPoint(event) {
            var canvas = this.$refs.signatureCanvas
            var rect = canvas.getBoundingClientRect()
            var clientX
            var clientY

            if (event.touches && event.touches.length > 0) {
                clientX = event.touches[0].clientX
                clientY = event.touches[0].clientY
            } else {
                clientX = event.clientX
                clientY = event.clientY
            }

            return {
                x: ((clientX - rect.left) * canvas.width) / rect.width,
                y: ((clientY - rect.top) * canvas.height) / rect.height
            }
        },
        startDrawing(event) {
            var canvas = this.$refs.signatureCanvas
            if (!canvas) return

            var ctx = canvas.getContext('2d')
            var point = this.getPoint(event)

            this.isDrawing = true
            this.hasStroke = true
            ctx.beginPath()
            ctx.moveTo(point.x, point.y)
        },
        draw(event) {
            if (!this.isDrawing) return

            var canvas = this.$refs.signatureCanvas
            var ctx = canvas.getContext('2d')
            var point = this.getPoint(event)

            ctx.lineTo(point.x, point.y)
            ctx.stroke()
        },
        stopDrawing() {
            this.isDrawing = false
        },
        clearCanvas() {
            this.setupCanvas()
        },
        confirmSignature() {
            var canvas = this.$refs.signatureCanvas
            if (!canvas || !this.hasStroke) return

            this.$emit('sign', {
                theme: this.theme,
                signPicture: canvas.toDataURL('image/png'),
                signDate: new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                signName: this.signName
            })

            this.closeModal()
        }
    }
}
</script>

<style scoped>
* {
    box-sizing: border-box;
}

.signature-card {
    border-radius: 8px;
    padding: 12px;
    min-height: 220px;
    border: 1px solid #e4e2dc;
    box-shadow: 0 3px 8px rgba(25, 25, 25, 0.04);
    transition: background 0.2s ease, border-color 0.2s ease;
}

.default-card {
    background: #ffffff;
    border-color: #e5e7eb;
}

.day-active {
    background: #f5f0e4;
    border-color: #edc68b;
}

.night-inactive {
    background: #ffffff;
    border-color: #d8dbe2;
}

.night-active {
    background: #f3e8ff;
    border-color: #a78bfa;
}

.card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
}

.title-group {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.icon-box {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.icon-box .pi-sun {
    background: #f3dfbf;
    color: #f6520b;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.icon-box .pi-moon {
    background: #d9ddf6;
    color: #7c3aed;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

h2 {
    margin: 2px 0 2px;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 700;
    color: #1f2430;
}

p {
    margin: 0;
    font-size: 12px;
    color: #66768c;
    font-weight: 500;
}

.status-check {
    width: 24px;
    height: 24px;
    color: #0f9f66;
    flex-shrink: 0;
    margin-top: 8px;
    font-size: 20px;
}

.info-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-top: 8px;
    max-width: 100%;
}

.info-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.label {
    display: block;
    font-size: 10px;
    color: #66768c;
    font-weight: 500;
}

.info-block strong {
    font-size: 12px;
    color: #1f2430;
    font-weight: 700;
}

.signature-box {
    margin-top: 8px;
    width: 100%;
    height: 76px;
    border-radius: 14px;
    background: #f8f8fa;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px dotted #e5e7eb;
    padding: 6px;
}

.signature-box:disabled {
    cursor: not-allowed;
}

.signature-box.clickable {
    cursor: pointer;
}

.signature-box.locked {
    opacity: 1;
}

.signature-image {
    max-width: 180px;
    max-height: 62px;
    object-fit: contain;
}

.warning-banner {
    margin-top: 8px;
    border: 1px solid #efcf67;
    background: #fffaf0;
    color: #a24700;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.4;
}

.empty-signature-space {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.signature-placeholder-line {
    width: 140px;
    border-bottom: 1.5px solid #cfd6de;
}

.signature-placeholder-text {
    font-size: 11px;
    color: #98a2b3;
    font-weight: 600;
    margin: 0;
    text-align: center;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(17, 24, 39, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    z-index: 1000;
}

.modal-card {
    width: min(640px, 100%);
    background: #ffffff;
    border-radius: 16px;
    padding: 16px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: #111827;
    font-weight: 700;
}

.close-btn {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 8px;
    background: #f3f4f6;
    cursor: pointer;
    font-size: 13px;
}

.modal-subtitle {
    margin: 8px 0 12px;
    color: #6b7280;
    font-size: 12px;
}

.pad-wrap {
    border: 1.5px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    background: #ffffff;
}

.signature-pad {
    width: 100%;
    height: 180px;
    display: block;
    touch-action: none;
    background: #ffffff;
}

.modal-actions {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.secondary-btn,
.primary-btn {
    border: none;
    border-radius: 10px;
    padding: 9px 14px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
}

.secondary-btn {
    background: #f3f4f6;
    color: #111827;
}

.primary-btn {
    background: #6d28d9;
    color: #ffffff;
}

@media (max-width: 640px) {
    .signature-card {
        padding: 10px;
        min-height: auto;
        border-radius: 12px;
    }

    .title-group {
        gap: 10px;
    }

    .icon-box,
    .icon-box .pi-sun,
    .icon-box .pi-moon {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        font-size: 14px;
    }

    h2 {
        font-size: 13px;
    }

    p {
        font-size: 11px;
    }

    .info-row {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .info-block strong {
        font-size: 12px;
    }

    .warning-banner {
        font-size: 10px;
        padding: 8px 10px;
    }

    .signature-placeholder-text {
        font-size: 10px;
    }

    .modal-card {
        padding: 12px;
        border-radius: 12px;
    }

    .modal-header h3 {
        font-size: 16px;
    }

    .signature-pad {
        height: 160px;
    }
}
</style>